// Centralized annotation presentation helpers (single source of truth)
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { humanizeRole } from 'common/helpers/humanize';
import { Task, Wrench } from '../../../common/images/FaIcons';
import NoteSvgNew from '../../../common/images/NoteSvgNew';
import AlarmFlagSvg from '../../../common/images/AlarmFlagSvg';
import HealthIconSvg from '../../../common/images/HealthIconSvg';
import MachineSvg from '../../../common/images/MachineSvg';

const DEFAULT_ICON_SIZE = 16;
const DEFAULT_ICON_COLOR = '#3C3D3A';

const ICON_BUILDERS = {
  task: ({ size, color, style, ...rest } = {}) => {
    const effectiveColor = color || '#3C3D3A';
    const baseStyle = {
      fontSize: `${size}px`,
      color: effectiveColor
    };
    return (
      <Task
        style={style ? { ...baseStyle, ...style } : baseStyle}
        {...rest}
      />
    );
  },
  note: ({ size, color, ...rest } = {}) => (
    <NoteSvgNew width={size} height={size} fill={color || '#3C3D3A'} {...rest} />
  ),
  repair: ({ size, color, style, ...rest } = {}) => {
    const effectiveColor = color || '#3C3D3A';
    const baseStyle = {
      fontSize: `${size}px`,
      color: effectiveColor
    };
    return (
      <Wrench
        style={style ? { ...baseStyle, ...style } : baseStyle}
        {...rest}
      />
    );
  },
  diagnostic_rule: ({ size, ...rest } = {}) => (
    <HealthIconSvg width={size} height={size} {...rest} />
  ),
  machine_condition_change: ({ size, color, ...rest } = {}) => (
    <MachineSvg width={size} height={size} fill={color || '#FF4F39'} {...rest} />
  ),
  alarm: ({ size, color, ...rest } = {}) => (
    <AlarmFlagSvg width={size} height={size} fill={color || '#3C3D3A'} {...rest} />
  )
};

// Plain text glyphs for non-React contexts (e.g., d3/svg text on charts)
export const TYPE_ICON_GLYPH = {
  task: '🛠️',
  note: '📄',
  repair: '⚙️',
  diagnostic_rule: '🔍',
  machine_condition_change: '🔧',
  alarm: '🚨'
};

export const TYPE_COLOR = {
  task: '#1B73E8',
  note: '#2CB34A',
  repair: '#6C3CF0',
  diagnostic_rule: '#9C27B0',
  machine_condition_change: '#607D8B',
  alarm: '#F44336'
};

export function getEffectiveType(type) {
  return type || 'note';
}

export function getAnnotationColor(annotation, { respectSeverity = true } = {}) {
  const type = getEffectiveType(annotation && annotation.type);
  if (respectSeverity && annotation && annotation.severity === 'critical') return '#D32F2F';
  if (respectSeverity && annotation && annotation.severity === 'high') return '#F57C00';
  return TYPE_COLOR[type] || '#757575';
}

export function getAnnotationIcon(type, options = {}) {
  const t = getEffectiveType(type);
  const builder = ICON_BUILDERS[t] || ICON_BUILDERS.note;
  const {
    size = DEFAULT_ICON_SIZE,
    color = DEFAULT_ICON_COLOR,
    style,
    ...rest
  } = options;
  return builder({ size, color, style, ...rest });
}

export function getAnnotationIconGlyph(type) {
  const t = getEffectiveType(type);
  return TYPE_ICON_GLYPH[t] || '📝';
}

export function getAnnotationIconMarkup(type, options = {}) {
  return renderToStaticMarkup(getAnnotationIcon(type, options));
}

export function getAnnotationIconDataUrl(type, options = {}) {
  const markup = getAnnotationIconMarkup(type, options);
  if (!markup) return null;
  const svg = markup.replace(/\s+/g, ' ').trim();
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function lightenHex(hex, amt = 12) {
  const clean = (hex || '').replace('#', '');
  const r = Math.min(255, parseInt(clean.slice(0, 2), 16) + amt);
  const g = Math.min(255, parseInt(clean.slice(2, 4), 16) + amt);
  const b = Math.min(255, parseInt(clean.slice(4, 6), 16) + amt);
  const toHex = v => v.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Format the created-by display for annotation tooltips.
 *
 * Priority:
 * 1) created_by_name (if provided by backend)
 * 2) created_by with email stripped to the part before '@'
 * 3) '-' as a fallback
 */
export function formatAnnotationUser(annotation) {
  if (!annotation) return '-';
  const direct = annotation.created_by_name || annotation.created_by;
  if (!direct) return '-';
  const value = String(direct);
  const atIndex = value.indexOf('@');
  return atIndex > 0 ? value.slice(0, atIndex) : value;
}

// True for the synthetic author on system-generated annotations (alarms,
// diagnostics). They have no human author, so no role to attribute.
export function isSystemAuthored(annotation) {
  const direct = annotation && (annotation.created_by_name || annotation.created_by);
  return String(direct || '').trim().toLowerCase() === 'system';
}

/**
 * Localized role of an annotation's author, or '' when there is none to show.
 *
 * `created_by_role` is optional by contract: system-generated annotations
 * (alarms, diagnostics) have no human author, and the backend leaves it null
 * whenever the per-account role lookup can't be resolved. Callers must degrade
 * to the bare name rather than rendering an empty pair of brackets.
 */
export function formatAnnotationUserRole(t, annotation) {
  if (!annotation || !annotation.created_by_role) return '';
  return humanizeRole(annotation.created_by_role, t) || '';
}

/**
 * Author attribution for tooltips: "name (Role)", or just "name" when the role
 * is unavailable.
 */
export function formatAnnotationUserWithRole(t, annotation) {
  // Localize the system author the same way getAnnotationUserLabel does -- the
  // two helpers render the same field in different tooltips, and without this
  // an alarm read "system" here and "System" (translated) there.
  if (isSystemAuthored(annotation)) {
    return t('annotations.user.system', { defaultValue: 'System' });
  }
  const name = formatAnnotationUser(annotation);
  const role = formatAnnotationUserRole(t, annotation);
  return role ? `${name} (${role})` : name;
}
